/**
 * Tujuan: Landing page utama — hero section, typewriter roles, stats, "What I Do", featured projects, social links
 * Caller: App.js route /
 * Dependensi: react-bootstrap, react-router-dom, typewriter-effect, react-icons
 * Main Functions: Home()
 * Side Effects: None
 * Changelog:
 *   2026-09 — Updated thesis title; added "View Details" link ke /project/thesis-absa
 */
import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import myImg from "../assets/avatar2.png";
import thesisImg from "../assets/thesis.png";
import tiketyukImg from "../assets/tiketyuk-logo-new.png";
import Typewriter from "typewriter-effect";

import {
  AiFillGithub,
  AiOutlineInstagram,
  AiOutlineRocket,
  AiOutlineProject,
  AiOutlineRead,
} from "react-icons/ai";

import { FaLinkedinIn, FaPaintBrush, FaChartBar, FaCode } from "react-icons/fa";

function Home() {
  return (
    <section>

      {/* ================= HERO ================= */}
      <Container fluid className="home-section" id="home">
        <Container className="section-container">
          <Row className="align-items-center">

            {/* LEFT — text content */}
            <Col md={7} className="text-center">

              <h1 className="heading">
                Hi There! <span className="wave">👋🏻</span>
              </h1>

              <h1 className="heading-name">
                I'M <span>NABILAH</span>
              </h1>

              {/* Rotating role — System.out.println("Role"); */}
              <div className="typewriter-container">
                <span className="system-out">System.out.println("</span>
                <span className="typewriter-text">
                  <Typewriter
                    options={{
                      strings: [
                        "UI/UX Designer",
                        "Data Analyst",
                        "Web Developer",
                        "MIS Master's Student",
                      ],
                      autoStart: true,
                      loop: true,
                      deleteSpeed: 35,
                      delay: 55,
                    }}
                  />
                </span>
                <span className="system-out">");</span>
              </div>

              {/* Thin separator */}
              <div className="typewriter-divider" />

              {/* Stats */}
              <div className="stats-row">
                <div className="stat-item">
                  <AiOutlineRead className="blue-icon" />
                  <p>MIS Master's Student</p>
                </div>

                <div className="stat-divider" />

                <div className="stat-item">
                  <AiOutlineProject className="blue-icon" />
                  <p>5+ Projects</p>
                </div>

                <div className="stat-divider" />

                <div className="stat-item">
                  <AiOutlineRocket className="blue-icon" />
                  <p>1 Yr UI/UX Exp</p>
                </div>
              </div>

            </Col>

            {/* RIGHT — avatar */}
            <Col md={5} className="text-center mt-4 mt-md-0">
              <img
                src={myImg}
                alt="Nabilah Putri"
                className="home-pic img-fluid"
              />
            </Col>

          </Row>
        </Container>
      </Container>

      {/* ================= WHAT I DO ================= */}
      <Container fluid className="home-about-section">
        <Container className="section-container">

          <h2>
            WHAT I <span className="blue">DO</span>
          </h2>

          {/* Skill list — no large identical cards */}
          <div className="skill-list">

            <div className="skill-item">
              <div className="skill-icon-wrap">
                <FaPaintBrush />
              </div>
              <div className="skill-body">
                <h4>UI/UX Design</h4>
                <p>User research, wireframing, and interactive prototyping in Figma. Internship experience at Telkom Indonesia and Metro TV.</p>
              </div>
            </div>

            <div className="skill-item">
              <div className="skill-icon-wrap">
                <FaChartBar />
              </div>
              <div className="skill-body">
                <h4>Data Analysis</h4>
                <p>End-to-end data processing — crawling, cleaning, SQL querying, and building dashboards with Python and Tableau.</p>
              </div>
            </div>

            <div className="skill-item">
              <div className="skill-icon-wrap">
                <FaCode />
              </div>
              <div className="skill-body">
                <h4>Web Development</h4>
                <p>Building functional web applications with Next.js, including ordering systems with API integrations and automated workflows.</p>
              </div>
            </div>

          </div>

          {/* ================= FEATURED — TIKETYUK ================= */}
          <div className="featured-section">

            <div className="featured-card featured-card-biz">
              <Row className="align-items-center">

                <Col md={8}>
                  <span className="featured-label featured-label-biz">Real Business Project</span>
                  <h3 className="featured-title">TiketYuk</h3>
                  <p className="featured-role">Founder &amp; Developer · Concert Ticket War &amp; Concierge Service</p>
                  <p className="featured-desc">
                    Built and operated a concert ticketing service handling 100+ ticket orders with a 4-person team.
                    Developed the ordering website and integrated customer submissions with operational workflows,
                    spreadsheet-based data management, and Discord notifications.
                  </p>
                  <div className="featured-actions">
                    <a href="https://www.tiketyuk.me/" target="_blank" rel="noreferrer" className="btn-primary btn-biz">
                      Visit Website
                    </a>
                    <Link to="/project/tiketyuk" className="btn-outline">
                      View Details
                    </Link>
                  </div>
                </Col>

                <Col md={4} className="text-center mt-4 mt-md-0">
                  <div className="featured-right">
                    <img
                      src={tiketyukImg}
                      alt="TiketYuk Logo"
                      className="featured-image"
                    />
                  </div>
                </Col>

              </Row>
            </div>

            {/* ================= THESIS ================= */}
            <div className="featured-card featured-card-thesis">
              <Row className="align-items-center">

                <Col md={8}>
                  <span className="thesis-label">Master's Thesis — In Progress</span>
                  <h3 className="featured-title">
                    Klasifikasi Multi-Aspect dalam Aspect-Based Sentiment Analysis (ABSA) pada Ulasan KlikIndomaret menggunakan Model Transformer
                  </h3>
                  <p className="featured-desc">
                    Penelitian tesis S2 yang membandingkan model Transformer (IndoBERT & IndoRoBERTa) untuk klasifikasi sentimen multi-aspek pada ulasan aplikasi KlikIndomaret — mencakup aspek Aplikasi, Pembayaran, Harga, Pelayanan, dan Produk.
                  </p>
                  <div className="thesis-tech-tags">
                    {["NLP", "ABSA", "IndoBERT", "IndoRoBERTa", "Transformer", "Python"].map((tag) => (
                      <span key={tag} className="thesis-tag">{tag}</span>
                    ))}
                  </div>
                  <div className="featured-actions" style={{ marginTop: "16px" }}>
                    <Link to="/project/thesis-absa" className="btn-outline">
                      View Details
                    </Link>
                  </div>
                </Col>

                <Col md={4} className="text-center mt-4 mt-md-0">
                  <div className="featured-right">
                    <img
                      src={thesisImg}
                      alt="Thesis Illustration"
                      className="featured-image"
                    />
                  </div>
                </Col>

              </Row>
            </div>

          </div>

          {/* ================= SOCIAL ================= */}
          <div className="social-section">
            <h2>
              FIND ME <span className="blue">ON</span>
            </h2>
            <p>
              Feel free to <span className="blue">connect</span> with me
            </p>

            <ul className="home-about-social-links">
              <li>
                <a href="https://github.com/nabilahintaka" target="_blank" rel="noreferrer" className="home-social-icons">
                  <AiFillGithub />
                </a>
              </li>
              <li>
                <a href="https://www.linkedin.com/in/nabilahintaka/" target="_blank" rel="noreferrer" className="home-social-icons">
                  <FaLinkedinIn />
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/nabilahintaka/" target="_blank" rel="noreferrer" className="home-social-icons">
                  <AiOutlineInstagram />
                </a>
              </li>
            </ul>
          </div>

        </Container>
      </Container>
    </section>
  );
}

export default Home;
